import { ErrorHandler, Injectable, Injector } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';

import { environment } from '../../../environments/environment';


@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

    constructor(private injector: Injector) { }

    handleError(error: any): void {

        if (environment.reportErrors && environment.sentry?.dsn) {
            Sentry.captureException(error.originalError || error);
        }

        const apiError = this.parseError(error);
        const unwrappedError = error.originalError || error;

        if (!environment.production) {
            this.showErrorInConsole(unwrappedError);
            this.showApiErrorInConsole(apiError);
        }
    }

    private showApiErrorInConsole(apiError: ApiError): void {
        if (console && console.group && console.error) {
            console.group('Api Error Group');
            console.error(apiError);
            console.error(apiError.message);
            if (apiError.errors) {
                console.error(apiError.errors);
            }
            if (apiError.detail) {
                console.error(apiError.detail);
            }
            if (apiError.stack) {
                console.error(apiError.stack);
            }
            console.groupEnd();
        }
    }

    private showErrorInConsole(error: any): void {
        if (console && console.group && console.error) {
            console.group('Error Group');
            console.error(error);
            console.groupEnd();
        }
    }

    private parseError(error: any): ApiError {
        const unexpectedErrorMessage = 'Hmm.. Something went wrong. Try again shortly or call us at 210-477-4714';
        const statusCode = error && error.status ? error.status : 0;

        if (statusCode === 0) {
            return { message: unexpectedErrorMessage, isError: true };
        }

        const body = this.extractErrorBody(error);

        if (body && body.Message) {
            return { message: body.Message, isError: true };
        }
        return <ApiError>(body || {});
    }

    private extractErrorBody(error: any): any {
        // Modern Angular HttpClient: HttpErrorResponse exposes the parsed body on .error
        if (error && error.error != null) {
            if (typeof error.error === 'string') {
                try { return JSON.parse(error.error); } catch { return { message: error.error, isError: true }; }
            }
            return error.error;
        }
        // Legacy Angular pre-4.3 Response object
        if (error && typeof error.json === 'function') {
            try { return error.json() || {}; } catch { return {}; }
        }
        return {};
    }


}
export interface ApiError {
    message: string;
    isError: boolean;

    errors?: ValidationError[];

    detail?: string;
    stack?: string;
}

export interface ValidationError {
    errorMessage: string;
    controlName: string;
    errorCode: string;
}
